import React, { useState, useEffect, useMemo, useRef } from "react";
import { Row, Col } from "react-bootstrap";
import Accordion from "../../Accordion";
import ReversedClaimsForm from "./ReversedClaimsForm";
import ErrorHandler from "./../../core/ErrorHandler";
import { GridColumn } from "@progress/kendo-react-grid";

import { ZIndexContext } from "@progress/kendo-react-common";
import SharedMainSelectableClientGrid from "shared/components/grids/clientGrid/SharedMainSelectableClientGrid";
import useGridButtons from "shared/components/grids/hooks/useGridButtons";
import { IsNullOrEmpty } from "shared/helpers/NullEmptyHelpers";
import useGridDataStateResetProcessing from "shared/hooks/useGridDataStateResetProcessing";

import {
  MoneyCell,
  AggregateMoneyFooterCell,
  AggregateNumberFooterCell,
  TooltipCell,
} from "../../grid/Kendo/CustomKendoGridCells";
import {
  DefaultColumnWidth,
  DefaultBooleanOrShortWidthColumnWidth,
} from "../../grid/Kendo/KendoGridAndColumnConstants";
import PaymentExceptionDetailModal from "./PaymentExceptionDetailModal";

export function ReversedClaims(props) {
  const { passedProps } = props;
  const user = passedProps.user.email;
  const DATA_ITEM_KEY = "pbid";

  const [submittedFormData, setSubmittedFormData] = useState(null);
  const [error, setError] = useState({});
  const [showViewReportModal, setShowViewReportModal] = useState(false);
  const [detailPayer, setDetailPayer] = useState([]);

  const [mainGridData, setMainGridData] = useState([]);
  const [mainGridAggregates, setMainGridAggregates] = useState([]);
  const [selectedState, setSelectedState] = React.useState({});
  const {
        gridDataStateResetProcessingInitiated,
        InitiateGridDataStateReset,
        GridDataStateResetCompleted: onGridDataStateResetCompleted,
      } = useGridDataStateResetProcessing();
  
  const clientGridRef = useRef();
  const { AddGridButton, gridButtons } = useGridButtons();
  const gridApiParams = useMemo(() => {
    if (IsNullOrEmpty(user)) {
      return {};
    }

    if (!submittedFormData) {
      return {};
    }
    //If user is not logged in, we cannot get the data  
    let params = {
      user: user,
      fromDate: submittedFormData.fromDate,
      toDate: submittedFormData.toDate,
      locations: submittedFormData.locations,
      payers: submittedFormData.payers,
      dateType: submittedFormData.dateType,
      checkNumber: submittedFormData.checkNumber,
      showArchiveRemitsOnly: submittedFormData.showArchiveRemitsOnly,
    };
    return params;
  }, [submittedFormData]);

  useEffect(() => {
    AddGridButton(
      "View Selected Reports",
      sendSelectablesToParent,
      true
    );
  }, []);

  const sendSelectablesToParent = () => {

    const selectedDataKeys = clientGridRef.current
      ? clientGridRef.current.GetSelectedGridDataKeys()
      : null;

    getReport(selectedDataKeys);
  }

  function setMainGridDataSource(allData) {
    if (allData) {
      setMainGridData(allData);
    }
  }
  const mainDataSource = () => {
    let dataSource = clientGridRef.current
      ? clientGridRef.current.GetAllGridData()
      : [];
    return dataSource;
  }

  const CustomActionCell = (props) => (
    <CustomCell {...props} color={"#015CAB"} />
  );

  function handleFormSubmit(e) {
    InitiateGridDataStateReset();
    setSubmittedFormData(e);
  }

  const sendAggregatesToParent = (aggregatesFromPaymentsGridSkeleton) => {
    setMainGridAggregates(aggregatesFromPaymentsGridSkeleton.data);
  };

  function HandlDetailClose() {
    setShowViewReportModal(false);
  }

  const CustomCell = (props) => {
    function onCellClick() {
      getReport([props.dataItem.pbid]);
    }
    return (
      <td
        {...props.tdProps}
        colSpan={1}
        style={{
          color: props.color,
        }}
      >
        <a
          className="action-cell"
          onClick={onCellClick}
          data-testid={"action-" + props.key}
        >
          View Report
        </a>
      </td>
    );
  };

  const MainGridAggregateMoneyFooterCell = (props) => {
    var options = {
      aggregateData: mainGridAggregates,
    };

    return AggregateMoneyFooterCell(props, options);
  };

  const MainGridAggregateNumberFooterCell = (props) => {
    var options = {
      aggregateData: mainGridAggregates,
    };

    return AggregateNumberFooterCell(props, options);
  };

  function getReport(selectedPayerIds = []) {
    if (selectedPayerIds.length < 1) {
      return alert("Please select a report to view");
    }
    const payers = [];
    const rows = [];
    let pbids = selectedPayerIds;
    let datasource = mainGridData;
    if (datasource.length < 1) {
      datasource = mainDataSource();
    }
    for (const pbid of pbids) {
      const foundObject = datasource.find((item) => item.pbid == pbid);
      if (foundObject) {
        payers.push(foundObject.payer);
        rows.push(foundObject);
      }
    }


    setDetailPayer(payers.join(","));
    setSelectedState(pbids)
    setShowViewReportModal(true);

  }
  return (
    <div className="feature">
      <ErrorHandler
        error={error}
        onClose={() => {
          setError({});
        }}
      />
      <Accordion defaultExpanded label="Search & Filter">
        <Row>
          <Col className="higher-zindex-filters">
            <ReversedClaimsForm
              handleFormSubmit={handleFormSubmit}
              auth={passedProps.auth}
            />
          </Col>
        </Row>
      </Accordion>
      <Row>
        <Col className="tight-grid multi-line-filter">
          <SharedMainSelectableClientGrid
            ref={clientGridRef}
            isSelectable={true}
            apiGetEndpoint={"api/PaymentException/GetReversedClaimsSummary"}
            apiParameters={gridApiParams}
            dataItemKey={DATA_ITEM_KEY}
            aggregateColumnSettings={
              [{ aggregate: "sum", field: "claimCount" },
              { aggregate: "sum", field: "totalDifferenceAmount" }]}
            sendAggregatesParentCallback={sendAggregatesToParent}
            exportFileNamePrefix={"PaymentExceptionReversedClaims"}
            gridButtons={gridButtons}
            onDataStateChangedCallback={() => {
              const allData = clientGridRef.current
                ? clientGridRef.current.GetAllGridData()
                : [];

              if (allData) {
                setMainGridDataSource(allData);
              }
            }}
             gridDataStateResetProcessingInitiated={
              gridDataStateResetProcessingInitiated
            }
            onGridDataStateResetCompleted={onGridDataStateResetCompleted}
          >
            <GridColumn
              key={""}
              field={""}
              title={"Action"}
              width={DefaultBooleanOrShortWidthColumnWidth()}
              filterable={false}
              sortable={false}
              cells={{
                data: CustomActionCell,
              }}
              headerCell={TooltipCell}
            />
            <GridColumn
              key={"payer"}
              field={"payer"}
              title={"Payer"}
              width={DefaultColumnWidth()}
              headerCell={TooltipCell}
            />
            <GridColumn
              key={"claimCount"}
              field={"claimCount"}
              title={"Rx Count"}
              width={DefaultColumnWidth()}
              filter={"numeric"}
              cells={{
                footerCell: MainGridAggregateNumberFooterCell,
              }}
              footerCell={MainGridAggregateNumberFooterCell}
              headerCell={TooltipCell}
            />
            <GridColumn
              key={"totalDifferenceAmount"}
              field={"totalDifferenceAmount"}
              title={"Total Difference Amount"}
              width={DefaultColumnWidth()}
              filter={"numeric"}
              cells={{
                data: MoneyCell,
                footerCell: MainGridAggregateMoneyFooterCell,
              }}
              footerCell={MainGridAggregateMoneyFooterCell}
              headerCell={TooltipCell}
            />
          </SharedMainSelectableClientGrid>
        </Col>
      </Row>
      <ZIndexContext.Provider value={10003}>
        <PaymentExceptionDetailModal
          submittedFormData={submittedFormData}
          selectedPayers={selectedState}
          title={detailPayer} show={showViewReportModal} onClose={HandlDetailClose}
          passedProps={passedProps}
          tabName={"ReversedClaims"}
        >
        </PaymentExceptionDetailModal>
      </ZIndexContext.Provider>
    </div>
  );
}
